import { Table, Tabs } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState } from "react";
import { useTranslate } from "../i18n";
import { Person } from "../model/person";
import { CrudToolbar } from "./CrudToolbar";
import { EmptyState } from "./EmptyState";

/**
 * The detail view of a person: their own fields, plus the people assigned to
 * them, as tabs.
 *
 * Each roster tab carries the shared action bar, so a multi-row selection reads
 * the same here as a single-row selection does on a list page.
 */
export const SelectPersons = (props: {
    columns: ColumnsType<Person>,
    showCaretakers: boolean,
    caretakers?: Person[],
    onCaretakerSearch?: (value: string) => void,
    onCaretakersAssign?: () => void,
    onCaretakersRemove?: (selectedPersons?: Person[]) => void,
    onCaretakerReload?: () => void,
    patients?: Person[],
    onPatientsSearch?: (value: string) => void,
    onPatientsAssign?: () => void,
    onPatientsRemove?: (selectedPersons?: Person[]) => void,
    onPatientsReload?: () => void,
    person?: JSX.Element,
    personTitle?: string
}) => {

    const t = useTranslate();
    const [caretakerSearchTerm, setCaretakerSearchTerm] = useState<string>("");
    const [selectedCaretakers, setSelectedCaretakers] = useState<Person[]>([]);
    const [selectedCaretakerKeys, setSelectedCaretakerKeys] = useState<React.Key[]>([]);
    const [patientSearchTerm, setPatientSearchTerm] = useState<string>("");
    const [selectedPatients, setSelectedPatients] = useState<Person[]>([]);
    const [selectedPatientKeys, setSelectedPatientKeys] = useState<React.Key[]>([]);
    const [activeTab, setActiveTab] = useState<string>("0");

    const onCaretakerSearch = () => {
        if (props.onCaretakerSearch != undefined && caretakerSearchTerm != "") {
            props.onCaretakerSearch(caretakerSearchTerm);
        }
    }

    const onCaretakersRemove = () => {
        if (props.onCaretakersRemove != undefined && selectedCaretakers.length > 0) {
            props.onCaretakersRemove(selectedCaretakers);
        }
    }

    const onSelectionCaretakersChange = (selectedRowKeys: React.Key[], persons: Person[]) => {
        setSelectedCaretakerKeys(selectedRowKeys);
        setSelectedCaretakers(persons);
    }

    const onPatientSearch = () => {
        if (props.onPatientsSearch != undefined && patientSearchTerm != "") {
            props.onPatientsSearch(patientSearchTerm);
        }
    }

    const onPatientsRemove = () => {
        if (props.onPatientsRemove != undefined && selectedPatients.length > 0) {
            props.onPatientsRemove(selectedPatients);
        }
    }

    const onSelectionPatientsChange = (selectedRowKeys: React.Key[], persons: Person[]) => {
        setSelectedPatientKeys(selectedRowKeys);
        setSelectedPatients(persons);
    }

    /** "3 people" — what the contextual strip names when several rows are held.
        The plural form is the language's own, not a bolted-on "s". */
    const countLabel = (persons: Person[]) =>
        persons.length === 1
            ? [persons[0].firstName, persons[0].lastName].filter(Boolean).join(" ")
            : t("assign.selection.count", { count: persons.length });

    const tabs = [];

    if (props.person) {
        tabs.push(
            <Tabs.TabPane tab={props.personTitle} key="3">
                {props.person}
            </Tabs.TabPane>
        )
    }

    tabs.push(
        <Tabs.TabPane tab={t("tabs.patients")} key="2">
            <CrudToolbar
                hasSelection={selectedPatients.length > 0}
                selectionLabel={countLabel(selectedPatients)}
                onClearSelection={() => onSelectionPatientsChange([], [])}
                onDelete={onPatientsRemove}
                deleteLabel={t("common.remove")}
                onAdd={props.onPatientsAssign}
                addLabel={t("common.assign")}
                onReload={props.onPatientsReload}
                hint={t("crud.hint.removePatients")}
                search={props.onPatientsSearch != null ? {
                    placeholder: t("list.search.patients"),
                    value: patientSearchTerm,
                    onChange: setPatientSearchTerm,
                    onSearch: onPatientSearch
                } : undefined}
            />
            <div className="card card--flush">
                <Table
                    rowKey={(person: Person) => person.id}
                    rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys: selectedPatientKeys,
                        onChange: onSelectionPatientsChange
                    }}
                    columns={props.columns}
                    dataSource={props.patients}
                    locale={{
                        emptyText: <EmptyState
                            title={t("empty.patients.title")}
                            hint={t("empty.patients.hint")}
                        />
                    }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                    scroll={{ x: 'max-content' }}
                />
            </div>
        </Tabs.TabPane>
    );

    if (props.showCaretakers) {
        tabs.push(
            <Tabs.TabPane tab={t("tabs.caretakers")} key="1">
                <CrudToolbar
                    hasSelection={selectedCaretakers.length > 0}
                    selectionLabel={countLabel(selectedCaretakers)}
                    onClearSelection={() => onSelectionCaretakersChange([], [])}
                    onDelete={onCaretakersRemove}
                    deleteLabel={t("common.remove")}
                    onAdd={props.onCaretakersAssign}
                    addLabel={t("common.assign")}
                    onReload={props.onCaretakerReload}
                    hint={t("crud.hint.removeCaretakers")}
                    search={props.onCaretakerSearch != null ? {
                        placeholder: t("list.search.caretakers"),
                        value: caretakerSearchTerm,
                        onChange: setCaretakerSearchTerm,
                        onSearch: onCaretakerSearch
                    } : undefined}
                />
                <div className="card card--flush">
                    <Table
                        rowKey={(person: Person) => person.id}
                        rowSelection={{
                            type: 'checkbox',
                            selectedRowKeys: selectedCaretakerKeys,
                            onChange: onSelectionCaretakersChange
                        }}
                        columns={props.columns}
                        dataSource={props.caretakers}
                        locale={{
                            emptyText: <EmptyState
                                title={t("empty.caretakers.title")}
                                hint={t("empty.caretakers.hint")}
                            />
                        }}
                        pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                        scroll={{ x: 'max-content' }}
                    />
                </div>
            </Tabs.TabPane>
        );
    }

    return (
        <Tabs defaultActiveKey={activeTab} type="line" onChange={setActiveTab}>
            {tabs}
        </Tabs>
    );
}
